// ---------------------------------------------------------------------------
// @firesquirrel/core-domain — Inspection response collector (pure functions)
//
// This module implements the shared merge/rollup/transition semantics that
// both tenant-web and mobile-pwa use to persist inspection responses. It has
// no I/O: the apps wrap these pure functions with their own Dexie-backed
// services.
//
// Canonical rules enforced here:
//
//   1. The case-level `responses` map is the source of truth for the final
//      combined inspection result.
//   2. Each inspection event's `responseSnapshot` captures what that event
//      contributed to the canonical state.
//   3. Hidden values are retained and flagged — never silently dropped.
//   4. Not-applicable is a widget-only state, kept distinct from hidden.
//   5. Only meaningful persisted transitions are recorded; ephemeral UI
//      edits are not tracked.
//   6. The attachment target for a capture is determined by the WIDGET,
//      never by the enclosing form or section.
// ---------------------------------------------------------------------------

import type { ResolvedFormState } from './types';
import type {
  AttachmentRefLike,
  CanonicalQuestionResponse,
  EventResponseSnapshot,
  FormVisibilityState,
  HiddenReason,
  ResponseTransition,
  RuleEffect,
  SectionVisibilityState,
  Violation,
  ViolationOccurrence,
} from './responseTypes';

// ---------------------------------------------------------------------------
// Equality helpers — structural so we can compare values / arrays / objects.
// ---------------------------------------------------------------------------

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

// ---------------------------------------------------------------------------
// mergeResponseTransition
// ---------------------------------------------------------------------------

export interface MergeTransitionInput {
  eventIndex: number;
  actorId: string;
  now?: string;

  nextValue: unknown;
  nextIsNA?: boolean;
  nextIsHidden?: boolean;
  nextHiddenReason?: HiddenReason;

  /** New attachments observed on this transition — appended, not replaced. */
  addedAttachments?: AttachmentRefLike[];

  /** Optional reason ("autosave", "reinspection correction", ...). */
  reason?: string;

  /** When a rules-engine evaluation caused this transition. */
  triggeredByRuleId?: string;

  /** When an upstream response's change propagated here. */
  triggeredByFieldId?: string;

  /**
   * Provenance of the new value — 'user' for inspector-initiated edits,
   * 'dataBound' for resolved data-bound widget values, 'ruleComputed' for
   * rule-derived values. Stamped onto `CanonicalQuestionResponse.valueSource`.
   */
  valueSource?: {
    kind: 'user' | 'dataBound' | 'ruleComputed';
    ruleId?: string;
    boundField?: string;
  };
}

/**
 * Merge a new persisted state into an existing canonical response, appending
 * one transition entry per dimension that changed (value, isNA, isHidden).
 * Returns the updated response — never mutates the input.
 *
 * Transient UI edits that land on the same value/NA/hidden do NOT append
 * a transition, which matches the agreed audit granularity rule.
 */
export function mergeResponseTransition(
  current: CanonicalQuestionResponse,
  input: MergeTransitionInput,
): CanonicalQuestionResponse {
  const now = input.now ?? new Date().toISOString();
  const nextIsNA = input.nextIsNA ?? current.isNA;
  const nextIsHidden = input.nextIsHidden ?? current.isHidden;

  const valueChanged = !valuesEqual(current.value, input.nextValue);
  const naChanged = current.isNA !== nextIsNA;
  const hiddenChanged = current.isHidden !== nextIsHidden;

  const attachmentsChanged =
    (input.addedAttachments?.length ?? 0) > 0;

  if (!valueChanged && !naChanged && !hiddenChanged && !attachmentsChanged) {
    return current;
  }

  // Append one transition per dimension that changed. This matches the
  // discriminated-union shape of ResponseTransition — each entry describes
  // exactly one kind of change.
  const transitions = current.transitions ? current.transitions.slice() : [];
  const common = {
    eventIndex: input.eventIndex,
    changedAt: now,
    changedBy: input.actorId,
    reason: input.reason,
    triggeredByRuleId: input.triggeredByRuleId,
    triggeredByFieldId: input.triggeredByFieldId,
  };
  if (valueChanged) {
    transitions.push({
      ...common,
      kind: 'value',
      previousValue: current.value,
      newValue: input.nextValue,
    });
  }
  if (naChanged) {
    transitions.push({
      ...common,
      kind: 'isNA',
      previousIsNA: current.isNA,
      newIsNA: nextIsNA,
    });
  }
  if (hiddenChanged) {
    transitions.push({
      ...common,
      kind: 'isHidden',
      previousIsHidden: current.isHidden,
      newIsHidden: nextIsHidden,
    });
  }

  // Append attachments — do not silently drop existing ones.
  const attachments = current.attachments ? current.attachments.slice() : [];
  if (input.addedAttachments?.length) {
    for (const ref of input.addedAttachments) {
      if (!attachments.some((a) => a._id === ref._id)) {
        attachments.push(ref);
      }
    }
  }

  return {
    ...current,
    value: input.nextValue,
    isNA: nextIsNA,
    isHidden: nextIsHidden,
    hiddenReason: input.nextHiddenReason ?? current.hiddenReason,
    naSetAt: naChanged && nextIsNA ? now : current.naSetAt,
    naSetBy: naChanged && nextIsNA ? input.actorId : current.naSetBy,
    attachments,
    transitions,
    valueSource: input.valueSource ?? current.valueSource,
    updatedAt: now,
    updatedBy: input.actorId,
  };
}

// ---------------------------------------------------------------------------
// Session-level transition recorder
//
// A small stateful wrapper around `mergeResponseTransition` so both apps can
// capture mid-session visibility / NA / value toggles in real time without
// each having to maintain their own `Record<fieldId, CanonicalQuestionResponse>`
// scratch map.
//
// Design:
//   - The recorder is seeded with the current canonical state at session
//     start (typically the rollup of prior events, or an empty object for
//     the first inspection).
//   - `record(fieldId, input)` merges a new persisted state in, appending
//     a transition if something actually changed.
//   - `getCanonicalResponses()` returns the accumulated canonical map,
//     ready to be handed to `buildEventResponseSnapshot` at completion.
//
// The recorder is a plain object (no React / store coupling) so it can be
// constructed in any layer and driven by either app's form-change handler.
// ---------------------------------------------------------------------------

export interface SessionTransitionRecorder {
  /**
   * Merge a field's new persisted state. Appends a transition entry iff
   * the value / NA / hidden state actually changed. Attachments added via
   * `input.addedAttachments` are deduplicated against existing ones.
   */
  record(fieldId: string, input: MergeTransitionInput): void;

  /**
   * Snapshot the accumulated canonical responses. Safe to call multiple
   * times; returns a shallow copy so callers cannot mutate recorder state.
   */
  getCanonicalResponses(): Record<string, CanonicalQuestionResponse>;

  /**
   * Reset the recorder back to the seed state. Useful when a session is
   * abandoned (e.g. user backs out of the conductor without completing).
   */
  reset(): void;
}

/**
 * Create a fresh session-level transition recorder seeded from the given
 * canonical response map. The seed is typically the case-level rollup of
 * prior events; for a first inspection pass an empty object is correct.
 */
export function createSessionTransitionRecorder(
  seed: Record<string, CanonicalQuestionResponse> = {},
): SessionTransitionRecorder {
  const initial = { ...seed };
  let state: Record<string, CanonicalQuestionResponse> = { ...seed };

  return {
    record(fieldId, input) {
      const current = state[fieldId] ?? createBlankCanonicalResponse(fieldId);
      const next = mergeResponseTransition(current, input);
      // Never persist an entry we never touched — mergeResponseTransition
      // returns the same reference when nothing changed, which lets us skip
      // writing to avoid polluting untouched-field state.
      if (next === current && !(fieldId in state)) return;
      state[fieldId] = next;
    },
    getCanonicalResponses() {
      return { ...state };
    },
    reset() {
      state = { ...initial };
    },
  };
}

/**
 * Construct a minimal canonical response for a field that has never been
 * touched in this session. The recorder uses this as the starting point
 * before delegating to `mergeResponseTransition`.
 */
function createBlankCanonicalResponse(
  fieldId: string,
): CanonicalQuestionResponse {
  return {
    fieldId,
    // The recorder does not know the field's form or widget type — these
    // are populated when the caller seeds the recorder from the template
    // snapshot, or when the first `record(fieldId, ...)` call lands (via
    // the merge path). Empty strings are deliberate placeholders the
    // collector's snapshot builder will overwrite from the template.
    formId: '',
    widgetType: '',
    value: undefined,
    isNA: false,
    isHidden: false,
    attachments: [],
    transitions: [],
  };
}

// ---------------------------------------------------------------------------
// rollupEventSnapshotsToCaseResponses
// ---------------------------------------------------------------------------

/**
 * Build the canonical case-level response state by folding ordered event
 * snapshots. Later events override earlier events field-by-field; hidden
 * values are preserved (not dropped) so they can become active again if
 * the visibility state changes.
 *
 * This is the formal implementation of "inspection one hands its responses
 * to inspection two, which makes its changes, and so on".
 */
export function rollupEventSnapshotsToCaseResponses(
  eventSnapshots: EventResponseSnapshot[],
): Record<string, CanonicalQuestionResponse> {
  const ordered = eventSnapshots
    .slice()
    .sort((a, b) => a.eventIndex - b.eventIndex);

  const rolled: Record<string, CanonicalQuestionResponse> = {};

  for (const snap of ordered) {
    for (const [fieldId, response] of Object.entries(snap.responses)) {
      // Later event fully replaces earlier event for a given field, but
      // transitions, response-level attachments, and violation occurrences
      // are accumulated for auditability.
      const priorTransitions = rolled[fieldId]?.transitions ?? [];
      const newTransitions = response.transitions ?? [];
      const mergedTransitions = dedupeTransitions([
        ...priorTransitions,
        ...newTransitions,
      ]);

      const priorAttachments = rolled[fieldId]?.attachments ?? [];
      const newAttachments = response.attachments ?? [];
      // If the later event explicitly defines attachments for this field
      // (response.attachments is an array, even if empty), any prior
      // attachment NOT present in the later set is considered removed by
      // the inspector and must be marked _deleted. This handles photos
      // removed during a reinspection.
      const hasExplicitAttachments = response.attachments !== undefined;
      const laterIds = hasExplicitAttachments
        ? new Set(newAttachments.map((a) => a._id))
        : null;
      const markedPrior = laterIds
        ? priorAttachments.map((att) => {
            if (att._deleted) return att;
            if (!laterIds.has(att._id)) {
              return { ...att, _deleted: true } as AttachmentRefLike;
            }
            return att;
          })
        : priorAttachments;
      const mergedAttachments = dedupeAttachmentsById([
        ...markedPrior,
        ...newAttachments,
      ]);

      const mergedViolation = mergeViolations(
        rolled[fieldId]?.violation,
        response.violation,
      );

      rolled[fieldId] = {
        ...response,
        transitions: mergedTransitions,
        attachments: mergedAttachments,
        violation: mergedViolation,
      };
    }
  }

  return rolled;
}

/**
 * Merge two snapshots of the same response's violation. Later wins on
 * scalar fields (status, severity, codes). Occurrences accumulate by `_id`
 * across events so reinspection can add occurrences without losing prior
 * ones, and occurrence attachments likewise accumulate.
 */
function mergeViolations(
  prior: Violation | undefined,
  next: Violation | undefined,
): Violation | undefined {
  if (!next) return prior;
  if (!prior) return next;
  // Different violation _ids shouldn't happen for the same response, but if
  // they do (e.g. schema migration), trust the later event outright.
  if (prior._id !== next._id) return next;

  const occById = new Map<string, ViolationOccurrence>();
  for (const occ of prior.occurrences ?? []) {
    occById.set(occ._id, occ);
  }
  for (const occ of next.occurrences ?? []) {
    const existing = occById.get(occ._id);
    if (!existing) {
      occById.set(occ._id, occ);
      continue;
    }
    // Same occurrence across two events: later wins for scalar fields,
    // attachments accumulate by _id.
    occById.set(occ._id, {
      ...occ,
      attachments: dedupeAttachmentsById([
        ...(existing.attachments ?? []),
        ...(occ.attachments ?? []),
      ]),
    });
  }

  return {
    ...next,
    occurrences: Array.from(occById.values()),
  };
}

function dedupeTransitions(list: ResponseTransition[]): ResponseTransition[] {
  const out: ResponseTransition[] = [];
  const seen = new Set<string>();
  for (const t of list) {
    const key = `${t.eventIndex}::${t.changedAt}::${t.changedBy}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

function dedupeAttachmentsById(list: AttachmentRefLike[]): AttachmentRefLike[] {
  // Later entries override earlier ones (last-write-wins) so that a later
  // event can mark an attachment `_deleted: true` and have that respected.
  const byId = new Map<string, AttachmentRefLike>();
  for (const ref of list) {
    byId.set(ref._id, ref);
  }
  return Array.from(byId.values());
}

// ---------------------------------------------------------------------------
// buildEventResponseSnapshot
// ---------------------------------------------------------------------------

export interface BuildEventSnapshotInput {
  eventIndex: number;
  capturedAt: string;
  capturedBy: string;

  /**
   * The canonical responses that this event is finalizing. Typically the
   * working responses map after all widget autosaves are flushed.
   */
  responses: Record<string, CanonicalQuestionResponse>;

  /** Optional resolved form states at finalization time (rules engine output). */
  resolvedFormStates?: Record<string, ResolvedFormState>;

  /** Optional prior form visibility to preserve lastChangedAt timestamps. */
  previousFormStates?: Record<string, FormVisibilityState>;
  previousSectionStates?: Record<string, SectionVisibilityState>;

  /**
   * Bounded audit record of rule effects that actually changed state during
   * this event. The rules engine populates this as it evaluates; collector
   * just threads it through into the snapshot.
   */
  ruleEffects?: RuleEffect[];
}

/**
 * Produce a snapshot of the inspection event's contribution to the case.
 * Form and section visibility are captured if the rules-engine output is
 * provided, preserving `lastChangedAt` when the state did not change.
 */
export function buildEventResponseSnapshot(
  input: BuildEventSnapshotInput,
): EventResponseSnapshot {
  const formStates: Record<string, FormVisibilityState> = {};
  const sectionStates: Record<string, SectionVisibilityState> = {};

  if (input.resolvedFormStates) {
    for (const [formId, formState] of Object.entries(input.resolvedFormStates)) {
      const prior = input.previousFormStates?.[formId];
      const isHidden = formState.hidden === true;
      const changed = prior?.isHidden !== isHidden;
      formStates[formId] = {
        formId,
        isHidden,
        hiddenReason: prior?.hiddenReason,
        lastChangedAt: changed ? input.capturedAt : prior?.lastChangedAt,
        lastChangedInEventIndex: changed
          ? input.eventIndex
          : prior?.lastChangedInEventIndex,
      };

      for (const [sectionId, sectionState] of Object.entries(formState.sections ?? {})) {
        const priorSection = input.previousSectionStates?.[sectionId];
        const sectionHidden = sectionState.hidden === true;
        const sectionChanged = priorSection?.isHidden !== sectionHidden;
        sectionStates[sectionId] = {
          sectionId,
          formId,
          isHidden: sectionHidden,
          hiddenReason: priorSection?.hiddenReason,
          lastChangedAt: sectionChanged ? input.capturedAt : priorSection?.lastChangedAt,
          lastChangedInEventIndex: sectionChanged
            ? input.eventIndex
            : priorSection?.lastChangedInEventIndex,
        };
      }
    }
  }

  const transitionsCount = Object.values(input.responses).reduce(
    (acc, r) => acc + (r.transitions?.length ?? 0),
    0,
  );

  return {
    eventIndex: input.eventIndex,
    capturedAt: input.capturedAt,
    capturedBy: input.capturedBy,
    responses: input.responses,
    formStates: Object.keys(formStates).length ? formStates : undefined,
    sectionStates: Object.keys(sectionStates).length ? sectionStates : undefined,
    ruleEffects: input.ruleEffects && input.ruleEffects.length > 0 ? input.ruleEffects : undefined,
    transitionsCount,
  };
}

// ---------------------------------------------------------------------------
// inheritPriorCanonicalResponses
// ---------------------------------------------------------------------------

/**
 * Baseline a new inspection event with the prior canonical case responses.
 * Matches the user's rule: "inspection one's responses become the baseline
 * for inspection two, which applies its changes, and so on".
 *
 * Returns a fresh map — the input is not mutated. Attachments and
 * transitions are carried forward for full audit fidelity.
 */
export function inheritPriorCanonicalResponses(
  priorCaseResponses: Record<string, CanonicalQuestionResponse>,
): Record<string, CanonicalQuestionResponse> {
  const out: Record<string, CanonicalQuestionResponse> = {};
  for (const [fieldId, response] of Object.entries(priorCaseResponses)) {
    out[fieldId] = {
      ...response,
      attachments: response.attachments ? response.attachments.slice() : [],
      transitions: response.transitions ? response.transitions.slice() : [],
    };
  }
  return out;
}

// ---------------------------------------------------------------------------
// Re-export the canonical types through the collector for convenience.
// ---------------------------------------------------------------------------

export type {
  AttachmentRefLike,
  CanonicalQuestionResponse,
  EventResponseSnapshot,
  FormVisibilityState,
  HiddenReason,
  ResponseTransition,
  RuleEffect,
  SectionVisibilityState,
  Violation,
  ViolationOccurrence,
};
